import React, { useEffect, useState } from "react";
import { API_URL, BILLING_API_URL, SITE, authFetch, fetchMe } from "../../utils/auth";
import TopBanner from "../TopBanner";
import Commission from "./Commission";
import LeadOverview from "./LeadOverview";
import RecentNotifications from "./RecentNotifications";
import ActionCenter from "./ActionCenter";

import "../../../css/partner/partnerHome.css";
import ToolSidebar from "../../utils/ToolSideBar";

const emptySummary = {
  total_commission_earned: "0.00",
  pending_approval: "0.00",
  pending_approval_projects: 0,
  pending_payout: "0.00",
  pending_payout_projects: 0,
  total_leads: 0,
};

export default function PartnerHome() {
  const [summary, setSummary] = useState(emptySummary);
  const [summaryStatus, setSummaryStatus] = useState("");
  const [profile, setProfile] = useState(null);
  const [isSuperuser, setIsSuperuser] = useState(localStorage.getItem("is_superuser") === "true");
  const [actions, setActions] = useState([]);
  const [actionsLoading, setActionsLoading] = useState(true);
  const [actionsError, setActionsError] = useState("");
  const [stripeLoading, setStripeLoading] = useState(false);

  async function loadDashboardData() {
    setSummaryStatus("");
    try {
      const [me, profileResponse, summaryResponse, actionsResponse] = await Promise.all([
        fetchMe().catch(() => null),
        authFetch(`${API_URL}/partner/profile-overview/`),
        authFetch(`${API_URL}/partner/commission-summary/`),
        authFetch(`${API_URL}/partner/dashboard-actions/`),
      ]);

      setIsSuperuser(Boolean(me?.is_superuser || localStorage.getItem("is_superuser") === "true"));

      if (profileResponse.ok) {
        setProfile(await profileResponse.json());
      }

      if (summaryResponse.ok) {
        const data = await summaryResponse.json();
        setSummary({ ...emptySummary, ...data });
        if (data.has_commission_ledger === false) {
          setSummaryStatus("Commission ledger is not connected yet; project counts are shown from referrals.");
        }
      } else {
        setSummary(emptySummary);
        setSummaryStatus("Dashboard metrics could not be refreshed. Showing zero values for now.");
      }

      if (actionsResponse.ok) {
        const data = await actionsResponse.json();
        setActions(Array.isArray(data.actions) ? data.actions : []);
        setActionsError("");
      } else {
        setActions([]);
        setActionsError("Unable to load action items.");
      }
    } catch (err) {
      console.error(err);
      setSummary(emptySummary);
      setSummaryStatus("Dashboard metrics could not be refreshed. Showing zero values for now.");
      setActionsError("Unable to load action items.");
    } finally {
      setActionsLoading(false);
    }
  }

  useEffect(() => {
    loadDashboardData();
  }, []);

  async function startStripeConnect() {
    setStripeLoading(true);
    setSummaryStatus("");
    try {
      const response = await authFetch(`${BILLING_API_URL}/connect/stripe/start/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Site": SITE,
        },
        body: JSON.stringify({ site: SITE }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || "Unable to start Stripe onboarding.");
      if (!data.url) throw new Error("Stripe did not return an onboarding link.");
      window.location.href = data.url;
    } catch (err) {
      console.error(err);
      setSummaryStatus(err.message || "Unable to start Stripe onboarding.");
    } finally {
      setStripeLoading(false);
    }
  }

  const shouldShowStripeConnect = summary?.has_commission_ledger === false || profile?.stripe_connected === false;
  const shouldShowActionCenter = !actionsLoading && !actionsError && actions.length > 0;

  return (
    <div className="partner-shell">
      <ToolSidebar active="dashboard" />
      <div className="partner-main">
        <TopBanner profile={profile} isSuperuser={isSuperuser} statusMessage={summaryStatus} />

        {summaryStatus && (
          <div className="partner-compact-status">
            <span>{summaryStatus}</span>
            <div className="partner-status-actions">
              {shouldShowStripeConnect && (
                <button type="button" className="primary" onClick={startStripeConnect} disabled={stripeLoading}>
                  {stripeLoading ? "Opening Stripe..." : "Connect Stripe to Get Paid"}
                </button>
              )}
              <button type="button" onClick={loadDashboardData}>Retry</button>
            </div>
          </div>
        )}

        <div className="kpi-grid">
          <Commission type="earned" summary={summary} />
          <Commission type="pending_approval" summary={summary} />
          <Commission type="pending_payout" summary={summary} />
          <Commission type="leads" summary={summary} />
        </div>

        {shouldShowActionCenter && (
          <ActionCenter actions={actions} loading={actionsLoading} error={actionsError} />
        )}

        <LeadOverview />

        <div className="grid-2">
          <RecentNotifications />
        </div>
      </div>
    </div>
  );
}


